<template>
  <div class="original-block">
    <article v-if="kind === 'fragment'" class="fragment-card" :class="{ open: expanded }">
      <div class="fragment-summary" role="button" tabindex="0" :aria-expanded="expanded.toString()" @click="toggleExpanded" @keydown.enter.prevent="toggleExpanded" @keydown.space.prevent="toggleExpanded">
        <h2>{{ source.title }}</h2>
        <p class="fragment-teaser">{{ source.entity?.teaser }}</p>
        <span class="read-cue">{{ expanded ? 'Close' : 'Read' }} <i class="fas" :class="expanded ? 'fa-minus' : 'fa-plus'" aria-hidden="true"></i></span>
      </div>
      <transition @before-enter="beforeBody" @enter="enterBody" @leave="leaveBody">
        <div v-if="expanded" class="fragment-body">
          <p v-for="(part, index) in displayParts" :key="index">{{ part }}</p>
        </div>
      </transition>
    </article>

    <article v-else-if="kind === 'experience'" class="view-experience-entry">
      <div class="experience-meta">
        <span>{{ source.entity?.period }}</span>
      </div>
      <div v-if="leadMedia" class="image-wrapper"><img :src="leadMedia" :alt="source.entity?.organisation || source.title"></div>
      <h3>{{ source.title }}</h3>
      <p class="experience-organisation">{{ source.entity?.organisation }}</p>
      <p class="experience-location">{{ source.entity?.location }}</p>
      <ul>
        <li v-for="(part, index) in displayParts" :key="index">{{ part }}</li>
      </ul>
    </article>

    <ul v-else-if="kind === 'project'" class="timeline view-project-timeline">
      <li class="timeline-event">
        <label class="timeline-event-icon"></label>
        <div class="timeline-event-copy">
          <p class="timeline-event-thumbnail">{{ source.entity?.period }}</p>
          <h3>{{ source.title }}</h3>
          <h4>{{ source.entity?.subtitle }}</h4>
          <h4 v-if="association">Associated with <b>{{ association }}</b></h4>
          <div v-if="source.entity?.mediaMode === 'logo' && source.media?.[0]" class="image-wrapper project-logo"><img :src="source.media[0].src" :alt="association || source.title" :style="projectLogoStyle"></div>
          <p v-for="(part, index) in displayParts" :key="index">{{ part }}</p>
          <div v-if="source.entity?.mediaMode !== 'logo' && source.media?.length" class="scroll-container project" aria-label="Project images">
            <img v-for="media in source.media" :key="media.src" :src="thumbFor(media.src)" :data-full-src="media.src" :alt="source.title" loading="lazy" decoding="async" @error="$emit('source-error', source.source_id)">
          </div>
        </div>
      </li>
    </ul>

    <article v-else-if="kind === 'early-project'" class="view-early-project">
      <p class="early-project-period">{{ source.entity?.period }}</p>
      <h3>{{ source.title }}</h3>
      <p v-for="(part, index) in displayParts" :key="index">{{ part }}</p>
      <div v-if="imageMedia.length" class="scroll-container early-project-gallery" :aria-label="`${source.title} images`">
        <img
          v-for="media in imageMedia"
          :key="media.src"
          :src="media.src"
          :data-full-src="media.fullSrc || media.src"
          :alt="media.alt || source.title"
          loading="lazy"
          decoding="async"
          @error="$emit('source-error', source.source_id)"
        >
      </div>
      <div v-if="videoMedia.length" class="early-project-videos">
        <video v-for="media in videoMedia" :key="media.src" controls preload="metadata">
          <source :src="media.src" type="video/mp4">
        </video>
      </div>
    </article>

    <div v-else-if="kind === 'gallery'" id="showcaseimages" class="scroll-container" aria-label="About gallery">
      <img v-for="media in source.media" :key="media.src" :src="media.src" :data-full-src="media.fullSrc || media.src" :alt="media.alt || 'A place from Henry’s gallery'" loading="lazy" decoding="async" @error="$emit('source-error', source.source_id)">
    </div>

    <div v-else-if="kind === 'publication'" class="publication-entry">
      <PublicationMeta :pub="publication" />
      <p v-for="(part, index) in displayParts" :key="index" class="publication-copy">{{ part }}</p>
      <div v-if="source.media?.length" class="scroll-container project" aria-label="Research figures">
        <img v-for="media in source.media" :key="media.src" :src="media.src" :alt="source.title" loading="lazy" decoding="async" @error="$emit('source-error', source.source_id)">
      </div>
    </div>

    <div v-else>
      <h3>{{ source.title }}</h3>
      <p v-for="(part, index) in selectedParts" :key="index">{{ part }}</p>
    </div>
  </div>
</template>

<script>
import { animate } from 'motion';
import PublicationMeta from '../PublicationMeta.vue';
import { thumbFor } from '../../data/projects';
import { decodeViewText } from '../../utils/viewText';

export default {
  name: 'ViewOriginalBlock',
  components: { PublicationMeta },
  props: {
    source: { type: Object, required: true },
    block: { type: Object, required: true },
    initiallyExpanded: { type: Boolean, default: false },
  },
  emits: ['source-error'],
  data() {
    return { expanded: this.initiallyExpanded };
  },
  computed: {
    kind() {
      return this.source?.entity?.kind || this.source?.view_kind || 'prose';
    },
    leadMedia() {
      return this.source?.media?.[0]?.src || '';
    },
    imageMedia() {
      return (this.source?.media || []).filter((media) => media.kind !== 'video');
    },
    videoMedia() {
      return (this.source?.media || []).filter((media) => media.kind === 'video');
    },
    selectedParts() {
      const parts = this.displayParts;
      if (!this.block.part_indices?.length) return parts;
      return this.block.part_indices.map((index) => parts[index]).filter(Boolean);
    },
    displayParts() {
      return (this.source?.parts || []).map(decodeViewText);
    },
    association() {
      return { insurance: 'HSBC', pwcRag: 'PwC', pwcItOps: 'PwC', pds: 'PDS Lab' }[this.source.entity?.id] || '';
    },
    projectLogoHeight() {
      const saved = String(this.source.entity?.logoHeight || '').trim();
      if (saved) return saved;
      return { insurance: '5vh', pwcRag: '6vh' }[this.source.entity?.id] || '5vh';
    },
    projectLogoStyle() {
      return { '--project-logo-height': this.projectLogoHeight };
    },
    publication() {
      return {
        title: this.source.title,
        status: this.source.entity?.status,
        authors: this.source.entity?.authors,
        links: this.source.entity?.links || [],
        self: 'H. Wang',
      };
    },
  },
  methods: {
    thumbFor,
    toggleExpanded() {
      this.expanded = !this.expanded;
    },
    beforeBody(el) {
      el.style.height = '0px';
      el.style.overflow = 'hidden';
      el.style.opacity = '0';
    },
    enterBody(el, done) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        el.style.height = '';
        el.style.overflow = '';
        el.style.opacity = '';
        done();
        return;
      }
      const controls = animate(el, { height: [0, el.scrollHeight], opacity: [0, 1] }, { type: 'spring', bounce: 0, duration: 0.35 });
      controls.finished.then(() => {
        el.style.height = '';
        el.style.overflow = '';
        done();
      });
    },
    leaveBody(el, done) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        done();
        return;
      }
      const controls = animate(el, { height: [el.getBoundingClientRect().height, 0], opacity: [1, 0] }, { type: 'spring', bounce: 0, duration: 0.3 });
      controls.finished.then(done);
    },
  },
};
</script>

<style scoped>
@import url('../../assets/css/list.css');

.original-block { color: inherit; }

.view-experience-entry { padding: 0.25rem 0; }
.experience-meta { display: flex; align-items: baseline; gap: 0.65rem; margin-bottom: 1.1rem; color: #969696; font-family: "Quattrocento Sans", sans-serif; font-size: 0.76rem; }
.view-experience-entry .image-wrapper { margin-bottom: 0.9rem; }
.view-experience-entry .image-wrapper img { max-width: 8rem; max-height: 3.5rem; object-fit: contain; }
.view-experience-entry h3 { margin: 0 0 0.45rem; font-size: 1.35rem; line-height: 1.25; }
.experience-organisation { margin: 0; color: #727272; font-size: 1rem; font-weight: 600; }
.experience-location { margin: 0.2rem 0 1rem; color: #969696; font-size: 0.88rem; }
.view-experience-entry ul { margin: 0; padding: 0; list-style: none; }
.view-experience-entry li { position: relative; margin-top: 0.65rem; padding-left: 0.8rem; line-height: 1.65; }
.view-experience-entry li::before { content: '·'; position: absolute; left: 0; color: #8a8a8a; }

.view-project-timeline { margin: 0; }
.view-project-timeline .timeline-event { margin: 0; }
.view-project-timeline .timeline-event-copy h4 + h4 { margin-top: 0.28em; }
.view-project-timeline .project-logo { max-width: 100%; }
.view-project-timeline .project-logo img {
  width: auto;
  max-width: 100%;
  height: var(--project-logo-height);
  object-fit: contain;
}
.view-project-timeline .scroll-container.project img { width: auto; height: auto; max-height: clamp(10rem, 28vw, 15rem); object-fit: contain; }

.view-early-project { padding: 0.15rem 0; }
.early-project-period { margin: 0 0 0.7rem !important; color: #929292; font-family: "Quattrocento Sans", sans-serif; font-size: 0.7rem; letter-spacing: 0.045em; text-transform: uppercase; }
.view-early-project h3 { margin: 0 0 1rem; font-size: 1.35rem; line-height: 1.28; }
.view-early-project > p:not(.early-project-period) { margin: 0.8rem 0; font-size: 0.98rem; line-height: 1.72; }
.early-project-gallery { margin-top: 1.1rem; }
.early-project-gallery img { width: auto; height: clamp(10rem, 28vw, 15rem); object-fit: contain; }
.early-project-videos { display: grid; gap: 0.8rem; margin-top: 1.1rem; }
.early-project-videos video { display: block; width: min(100%, 42rem); max-height: 26rem; background: #111; }

.fragment-card { --fragments-card-bg: rgba(0, 0, 0, 0.025); --fragments-heading: #333; --fragments-muted: #777; --fragments-body: #444; --fragments-cue: #555; --fragments-focus-ring: var(--site-accent); overflow: hidden; border: 0; border-radius: 0; background: var(--fragments-card-bg); }
.fragment-summary { padding: 1.05rem 1.1rem 1rem; cursor: pointer; outline: none; }
.fragment-summary:focus-visible { outline: 2px solid var(--fragments-focus-ring); outline-offset: -2px; }
.fragment-summary h2 { margin: 0 0 0.55rem; color: var(--fragments-heading); font-family: Georgia, "PT Serif", serif; font-size: 1.18rem; font-weight: 700; letter-spacing: -0.012em; line-height: 1.22; }
.fragment-teaser { margin: 0; color: var(--fragments-muted); font-family: "PT Serif", Georgia, serif; font-size: 0.98rem; line-height: 1.62; }
.read-cue { display: inline-flex; align-items: center; gap: 0.4rem; margin-top: 0.8rem; color: var(--fragments-cue); font-family: "Quattrocento Sans", sans-serif; font-size: 0.82rem; }
.read-cue i { font-size: 0.68rem; }
.fragment-body { padding: 0.1rem 1.1rem 1.15rem; }
.fragment-body p { margin: 1rem 0 0; color: var(--fragments-body); font-family: "PT Serif", Georgia, serif; font-size: 0.96rem; line-height: 1.78; white-space: pre-line; }
.publication-copy { margin: 0.85rem 0; line-height: 1.72; }
.original-block .scroll-container img { border-radius: 0; }

body.dark-mode .fragment-card { --fragments-card-bg: rgba(255, 255, 255, 0.035); --fragments-heading: #ccc; --fragments-muted: var(--site-text-secondary); --fragments-body: #cfcfcf; --fragments-cue: #ccc; --fragments-focus-ring: var(--site-accent); }
body.dark-mode .experience-organisation { color: #b8b8b8; }
body.dark-mode .experience-location,
body.dark-mode .experience-meta { color: #969696; }

@media (max-width: 560px) {
  .fragment-summary, .fragment-body { padding-left: 0.9rem; padding-right: 0.9rem; }
  .fragment-summary h2 { font-size: 1.08rem; }
}

@container (max-width: 560px) {
  .fragment-summary, .fragment-body { padding-left: 0.9rem; padding-right: 0.9rem; }
  .fragment-summary h2 { font-size: 1.08rem; }
}
</style>
